import React, { useEffect, useRef, useState } from 'react'
import { RouteComponentProps, useHistory, withRouter } from 'react-router-dom'
import { toast } from 'react-toastify'

import {
  Accordion,
  Botao,
  Breadcrumb,
  Cabecalho,
  Carregando,
  ErroLayoutContainer,
  IconeBack,
  Link,
  MenuContexto,
  MenuContextoRef
} from 'src/componentes'
import { Container as ContainerErroLayout } from 'src/componentes/erro-container-layout/styles'
import { FuncoesDataHora, FuncoesMoeda } from 'src/componentes/funcoes'
import { MenuContextoOpcao } from 'src/componentes/menu-contexto/menu-contexto-opcao'
import {
  RotasCondicaoEspecial,
  RotasCursos,
  RotasTurmas
} from 'src/rotas/academico'
import { RotasAluno } from 'src/rotas/aluno'
import { Api } from 'src/servicos'
import { CondicaoEspecialDetalheDto } from 'src/servicos/api'
import {
  CorStatusCondicaoEspecial,
  DestinoFormaPagamento,
  StatusCondicaoEspecial,
  StatusCondicaoEspecialPorExtenso
} from 'src/tipos'

import { descreverFormaPagamento } from '../descrever-forma-pagamento'
import {
  ModalAlterarStatus,
  ModalAlterarStatusRef
} from '../componentes/modal-alterar-status'
import {
  ModalCancelar,
  ModalCancelarRef
} from '../componentes/modal-cancelar'
import { copiarLinkDoAluno } from '../link-aluno'
import { descreverItemHistorico } from './historico'
import { motivoEdicaoBloqueada } from '../motivo-edicao-bloqueada'
import {
  BadgeStatus,
  BotaoAcoes,
  ContainerAcoes,
  Campo,
  Conteudo,
  ContainerLink,
  Grade,
  ItemTimeline,
  LinkExterno,
  SemDados,
  Tabela,
  TituloBloco,
  TituloComStatus,
  Timeline
} from './styles'

const exibirTexto = (valor?: string | number): string =>
  valor === null || valor === undefined || valor === '' ? '—' : String(valor)

const exibirData = (valor?: string): string =>
  valor ? FuncoesDataHora.dataStringFormatadaComBarras(valor) : '—'

const exibirMoeda = (valor?: number): string =>
  valor === null || valor === undefined ? '—' : FuncoesMoeda.formatarMoeda(valor)

const CampoInfo: React.FC<{ rotulo: string; valor: React.ReactNode }> = ({
  rotulo,
  valor
}) => (
  <Campo>
    <span className="rotulo">{rotulo}</span>
    <span className="valor">{valor}</span>
  </Campo>
)

const PaginaDetalheCondicaoEspecial: React.FC<
  RouteComponentProps<{ id: string }>
> = ({ match }) => {
  const history = useHistory()
  const id = match.params.id

  const modalCancelarRef = useRef<ModalCancelarRef>(null)
  const modalAlterarStatusRef = useRef<ModalAlterarStatusRef>(null)
  const menuAcoesRef = useRef<MenuContextoRef>(null)

  const [condicao, definirCondicao] = useState<CondicaoEspecialDetalheDto>(null)
  const [pronto, definirPronto] = useState(false)
  const [erro, definirErro] = useState<string>(null)
  const [enviandoConvite, definirEnviandoConvite] = useState(false)

  const carregar = async () => {
    try {
      definirErro(null)
      const dados = await Api.ObterCondicaoEspecialPorId(id)
      definirCondicao(dados)
      definirPronto(true)
    } catch {
      definirErro('Não foi possível carregar a condição especial.')
    }
  }

  useEffect(() => {
    carregar()
  }, [id]) //eslint-disable-line

  const enviarConvite = async () => {
    definirEnviandoConvite(true)

    try {
      const resposta = await Api.EnviarConviteCondicaoEspecial(id)
      toast(`Convite enviado para ${resposta.email}.`, { type: 'success' })
      carregar()
    } catch (excecao) {
      toast(
        'Não foi possível enviar o convite. Confira o e-mail e tente novamente.',
        { type: 'error' }
      )
    } finally {
      definirEnviandoConvite(false)
    }
  }

  if (erro) {
    return (
      <ContainerErroLayout>
        <p>{erro}</p>
        <Botao
          type="button"
          texto="Tentar novamente"
          tema="Secundario"
          onClick={carregar}
        />
      </ContainerErroLayout>
    )
  }

  if (!pronto || !condicao) {
    return <Carregando texto="Carregando condição especial ..." />
  }

  const chaveStatus = condicao.status as keyof typeof StatusCondicaoEspecial
  const corStatus = CorStatusCondicaoEspecial[chaveStatus]
  const textoStatus =
    condicao.statusPorExtenso ??
    StatusCondicaoEspecialPorExtenso[chaveStatus] ??
    condicao.status

  // A expirada sem aluno é editável (reabre ao estender a validade), mas trocar
  // o status à mão a deixaria "viva" com a validade ainda vencida.
  const podeAlterarStatus =
    condicao.podeEditar && condicao.status !== StatusCondicaoEspecial.Expirada

  // Mesmas condições do backend (PropostaMatricula.PodeEnviarConvite): link ainda
  // utilizável e um endereço para onde enviar.
  const statusComLinkValido: string[] = [
    StatusCondicaoEspecial.Iniciada,
    StatusCondicaoEspecial.EmNegociacao,
    StatusCondicaoEspecial.EnviadaAluno,
    StatusCondicaoEspecial.EmAndamento
  ]
  const podeEnviarConvite =
    !!condicao.emailConvite &&
    statusComLinkValido.includes(condicao.status) &&
    !condicao.expirada
  const motivoConviteBloqueado = podeEnviarConvite
    ? `Envia o link para ${condicao.emailConvite}.`
    : !condicao.emailConvite
      ? 'Informe o e-mail de convite (edite a condição) para enviar o link.'
      : 'O link não está mais disponível para envio.'

  const opcoesEntrada =
    condicao.plano?.opcoes?.filter(
      o => o.destino === DestinoFormaPagamento.Entrada
    ) ?? []
  const opcoesRestante =
    condicao.plano?.opcoes?.filter(
      o => o.destino === DestinoFormaPagamento.Restante
    ) ?? []

  /**
   * Entrada mais barata do plano, ou 0 quando não há entrada.
   *
   * É a base do desconto por linha: o aluno sempre paga uma entrada mais um
   * parcelamento, então o desconto de uma opção de restante só existe em
   * relação a alguma entrada. Usamos a menor porque é a que produz o maior
   * desconto — e é a combinação pré-selecionada na tela do aluno.
   */
  const menorEntrada = opcoesEntrada.length
    ? Math.min(...opcoesEntrada.map(o => o.valor))
    : 0

  /**
   * Desconto de uma opção de restante: valor de tabela menos (entrada +
   * restante). Mesma fórmula do selo da tela do aluno (`publica/calculos.ts`).
   *
   * Não se aplica às opções de ENTRADA: a entrada é só uma parte do negócio, e
   * `valorTabela − valorDaEntrada` não quer dizer nada — era o bug que fazia uma
   * entrada de R$ 500 aparecer com "R$ 7.700 de desconto" num curso de R$ 8.200.
   */
  const descontoDaOpcao = (valorTabela: number, valorRestante: number) => {
    const desconto = valorTabela - (menorEntrada + valorRestante)

    return desconto > 0 ? desconto : 0
  }

  /**
   * Desconto do plano como um todo, para o bloco de composição.
   *
   * Quando há mais de uma combinação possível, mostra a faixa: o consultor
   * cadastra parcelamentos com valores diferentes, então o desconto varia
   * conforme o que o aluno escolher.
   */
  const descreverDesconto = (
    valorTabela: number,
    entrada: typeof opcoesEntrada,
    restante: typeof opcoesRestante
  ): string => {
    if (!restante.length) return '—'

    // Sem entrada cadastrada, o total é só o restante.
    const valoresEntrada = entrada.length ? entrada.map(o => o.valor) : [0]

    const descontos = valoresEntrada
      .flatMap(valorEntrada =>
        restante.map(o => valorTabela - (valorEntrada + o.valor))
      )
      .map(desconto => (desconto > 0 ? desconto : 0))

    const menor = Math.min(...descontos)
    const maior = Math.max(...descontos)

    if (maior <= 0) return 'Sem desconto'

    return menor === maior
      ? exibirMoeda(menor)
      : `${exibirMoeda(menor)} a ${exibirMoeda(maior)}`
  }

  const renderizarTabelaOpcoes = (
    titulo: string,
    lista: typeof opcoesEntrada,
    /**
     * Só o restante mostra desconto. Na entrada a coluna não tem significado
     * (ver `descontoDaOpcao`).
     */
    comDesconto = false
  ) => (
    <>
      <TituloBloco>{titulo}</TituloBloco>
      {lista.length === 0 ? (
        <SemDados>Nenhuma opção cadastrada.</SemDados>
      ) : (
        <Tabela>
          <thead>
            <tr>
              <th className="texto">Forma de pagamento</th>
              <th className="numero">Valor total</th>
              <th className="numero">Parcelas</th>
              <th className="numero">Valor da parcela</th>
              {comDesconto && <th className="numero">Desconto</th>}
            </tr>
          </thead>
          <tbody>
            {lista.map((opcao, indice) => (
              <tr
                /*
                 * A mesma forma de pagamento pode ter varios parcelamentos
                 * (6x, 12x, 24x), entao destino+forma nao identifica a linha.
                 */
                key={`${opcao.destino}-${opcao.formaPagamento}-${opcao.quantidadeParcelas}`}
                className={indice % 2 === 1 ? 'par' : ''}
              >
                <td className="texto">{descreverFormaPagamento(opcao)}</td>
                <td className="numero">{exibirMoeda(opcao.valor)}</td>
                <td className="numero">{opcao.quantidadeParcelas}</td>
                <td className="numero">{exibirMoeda(opcao.valorParcela)}</td>
                {comDesconto && (
                  <td className="numero">
                    {exibirMoeda(
                      descontoDaOpcao(
                        condicao.plano?.valorTabela ?? 0,
                        opcao.valor
                      )
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </Tabela>
      )}
    </>
  )

  return (
    <>
      <ContainerLink>
        <Link
          texto="Voltar"
          icone={IconeBack}
          acaoVoltar={() => history.goBack()}
        />
      </ContainerLink>
      <Cabecalho>
        <TituloComStatus>
          <Breadcrumb
            titulo={`Condição especial ${condicao.numeroProtocolo}`}
            atalhos={[
              { texto: 'Financeiro' },
              {
                texto: 'Condição especial',
                acao: () => history.push(RotasCondicaoEspecial.Lista)
              },
              { texto: condicao.numeroProtocolo }
            ]}
          />
          {corStatus && (
            <BadgeStatus fundo={corStatus.fundo} corTexto={corStatus.texto}>
              {textoStatus}
            </BadgeStatus>
          )}
        </TituloComStatus>

        <ContainerAcoes>
          <MenuContexto
            ref={menuAcoesRef}
          posicao="direita"
          abrirComClick
          botao={<BotaoAcoes type="button">Ações</BotaoAcoes>}
        >
          <MenuContextoOpcao
            onClick={() => {
              menuAcoesRef.current?.fechar()
              copiarLinkDoAluno(condicao.id)
            }}
          >
            Copiar link do aluno
          </MenuContextoOpcao>
          <MenuContextoOpcao
            disabled={!podeEnviarConvite || enviandoConvite}
            title={motivoConviteBloqueado}
            onClick={() => {
              menuAcoesRef.current?.fechar()
              enviarConvite()
            }}
          >
            Enviar link por e-mail
          </MenuContextoOpcao>
          <MenuContextoOpcao
            disabled={!condicao.podeEditar}
            title={
              condicao.podeEditar
                ? undefined
                : motivoEdicaoBloqueada(condicao.status)
            }
            onClick={() => {
              menuAcoesRef.current?.fechar()
              history.push(`${RotasCondicaoEspecial.Edicao}/${condicao.id}`)
            }}
          >
            Editar
          </MenuContextoOpcao>
          <MenuContextoOpcao
            disabled={!podeAlterarStatus}
            title={
              podeAlterarStatus
                ? undefined
                : condicao.status === StatusCondicaoEspecial.Expirada
                  ? 'Para reabrir a condição, edite e estenda a validade do link.'
                  : 'Os status seguintes são definidos pelo sistema.'
            }
            onClick={() => {
              menuAcoesRef.current?.fechar()
              modalAlterarStatusRef.current?.abrir(condicao.status)
            }}
          >
            Alterar status
          </MenuContextoOpcao>
          <MenuContextoOpcao
            disabled={!condicao.podeCancelar}
            title={
              condicao.podeCancelar
                ? undefined
                : 'Não é possível cancelar depois que o aluno assumiu.'
            }
            onClick={() => {
              menuAcoesRef.current?.fechar()
              modalCancelarRef.current?.abrir()
            }}
          >
            Cancelar
          </MenuContextoOpcao>
          </MenuContexto>
        </ContainerAcoes>
      </Cabecalho>

      <Conteudo>
        <Accordion titulo="Negociação" iniciarAberto>
          <Grade>
            <CampoInfo
              rotulo="Protocolo"
              valor={exibirTexto(condicao.numeroProtocolo)}
            />
            <CampoInfo rotulo="Status" valor={textoStatus} />
            <CampoInfo
              rotulo="Curso"
              valor={
                condicao.cursoId ? (
                  <LinkExterno
                    href={`${RotasCursos.Edicao}/${condicao.cursoId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {exibirTexto(condicao.curso)}
                  </LinkExterno>
                ) : (
                  exibirTexto(condicao.curso)
                )
              }
            />
            <CampoInfo
              rotulo="Turma"
              valor={
                condicao.turmaId ? (
                  <LinkExterno
                    href={`${RotasTurmas.Edicao}/${condicao.turmaId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {exibirTexto(condicao.turma)}
                  </LinkExterno>
                ) : (
                  exibirTexto(condicao.turma)
                )
              }
            />
            <CampoInfo
              rotulo="Aluno / contato"
              valor={exibirTexto(condicao.nomeAluno ?? condicao.nomeContato)}
            />
            <CampoInfo
              rotulo="CPF informado"
              valor={exibirTexto(condicao.cpfAluno)}
            />
            <CampoInfo
              rotulo="E-mail de convite"
              valor={exibirTexto(condicao.emailConvite)}
            />
            <CampoInfo
              rotulo="Consultor"
              valor={exibirTexto(condicao.consultor)}
            />
            <CampoInfo
              rotulo="Validade do link"
              valor={
                condicao.expirada
                  ? `${exibirData(condicao.validadeAte)} (vencida)`
                  : exibirData(condicao.validadeAte)
              }
            />
            <CampoInfo
              rotulo="Aberta em"
              valor={exibirData(condicao.dataAbertura)}
            />
            <CampoInfo
              rotulo="Última alteração"
              valor={exibirData(condicao.dataUltimaAlteracao)}
            />
            {condicao.motivoCancelamento && (
              <CampoInfo
                rotulo="Motivo do cancelamento"
                valor={exibirTexto(condicao.motivoCancelamento)}
              />
            )}
          </Grade>
        </Accordion>

        <Accordion titulo="Composição" iniciarAberto>
          {condicao.plano ? (
            <>
              <Grade>
                <CampoInfo
                  rotulo="Valor de tabela"
                  valor={exibirMoeda(condicao.plano.valorTabela)}
                />
                <CampoInfo
                  rotulo="Taxa de matrícula"
                  valor={exibirMoeda(condicao.plano.taxaMatricula)}
                />
                <CampoInfo
                  rotulo="Taxa isenta"
                  valor={condicao.plano.taxaMatriculaInclusa ? 'Sim' : 'Não'}
                />
                <CampoInfo
                  rotulo="Tem entrada"
                  valor={condicao.plano.temEntrada ? 'Sim' : 'Não'}
                />
                <CampoInfo
                  rotulo="Início da recorrência"
                  valor={
                    condicao.plano.dataInicioRecorrencia
                      ? exibirData(condicao.plano.dataInicioRecorrencia)
                      : 'Cobrada junto da matrícula'
                  }
                />
                <CampoInfo
                  rotulo="Desconto concedido"
                  valor={descreverDesconto(
                    condicao.plano.valorTabela,
                    opcoesEntrada,
                    opcoesRestante
                  )}
                />
              </Grade>

              {condicao.plano.temEntrada &&
                renderizarTabelaOpcoes('Opções de entrada', opcoesEntrada)}
              {renderizarTabelaOpcoes(
                'Opções para o restante',
                opcoesRestante,
                true
              )}
            </>
          ) : (
            <SemDados>Composição não cadastrada.</SemDados>
          )}
        </Accordion>

        {(condicao.alunoId || condicao.matriculaGeradaId) && (
          <Accordion titulo="Matrícula gerada" iniciarAberto={false}>
            <Grade>
              <CampoInfo
                rotulo="Aluno"
                valor={exibirTexto(condicao.nomeAluno)}
              />
              <CampoInfo
                rotulo="Matrícula"
                valor={exibirTexto(condicao.matriculaGeradaId)}
              />
              {/*
                Painel financeiro do aluno, como a listagem de alunos faz
                (`painel-financeiro/{usuarioId}`). Leva ao painel com todas as
                matrículas dele, e não direto a esta: a rota por matrícula exige
                modalidade e nome do curso. Só aparece com o id do usuário —
                propostas antigas, sem ele, ficariam com um link quebrado.
              */}
              {condicao.usuarioAlunoId && (
                <CampoInfo
                  rotulo="Financeiro"
                  valor={
                    <LinkExterno
                      href={`${RotasAluno.PainelFinanceiro}/${condicao.usuarioAlunoId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Abrir painel financeiro
                    </LinkExterno>
                  }
                />
              )}
            </Grade>
          </Accordion>
        )}

        <Accordion titulo="Histórico de status" iniciarAberto>
          <Timeline>
            {(condicao.historico ?? []).length === 0 && (
              <SemDados>Nenhuma alteração de status registrada.</SemDados>
            )}
            {(condicao.historico ?? []).map(historico => {
              const linha = descreverItemHistorico(historico)

              return (
                <ItemTimeline key={historico.id}>
                  <span className="titulo">{linha.titulo}</span>
                  <span className="meta">
                    {FuncoesDataHora.dataHoraToLocaleString(historico.dataHora)}
                    {linha.autor ? ` · ${linha.autor}` : ''}
                  </span>
                  {linha.observacao && (
                    <span className="observacao">{linha.observacao}</span>
                  )}
                </ItemTimeline>
              )
            })}
          </Timeline>
        </Accordion>
      </Conteudo>

      <ModalCancelar
        ref={modalCancelarRef}
        backdrop
        condicaoId={condicao.id}
        aoCancelar={() => {
          toast('Condição especial cancelada.', { type: 'success' })
          carregar()
        }}
      />

      <ModalAlterarStatus
        ref={modalAlterarStatusRef}
        backdrop
        condicaoId={condicao.id}
        aoAlterar={() => {
          toast('Status alterado com sucesso.', { type: 'success' })
          carregar()
        }}
      />
    </>
  )
}

export default withRouter(
  (props: RouteComponentProps<{ id: string }>): JSX.Element => (
    <ErroLayoutContainer.Provider>
      <PaginaDetalheCondicaoEspecial {...props} />
    </ErroLayoutContainer.Provider>
  )
)
